import { useCallback, useEffect, useMemo, useState } from 'react';

// Per-viewer, not per-room - how far ahead of the actual audio the computed
// position runs varies by THIS listener's own network/jitter buffer, not by
// what room or song is playing, so it belongs in localStorage exactly like
// ScreenShareOverlay.js's remembered volume.
const OFFSET_STORAGE_KEY = 'lyricsSyncOffsetSec';
// musicSession.js's positionSec counts frames as they're handed to the
// LiveKit track, not as they reach the listener's ear - WebRTC jitter
// buffering/decode adds real (if usually small) latency on top, which
// without correction shows lyrics slightly ahead of what's actually heard.
// This is a starting point, not a measured constant - the user-facing sync
// control (LyricsPanel's offset slider) is the actual fix; this default
// just aims to need less of it.
const DEFAULT_SYNC_OFFSET_SEC = 0.3;
const TICK_MS = 150;

function loadStoredOffset() {
    try {
        const stored = parseFloat(localStorage.getItem(OFFSET_STORAGE_KEY));
        return Number.isFinite(stored) ? stored : DEFAULT_SYNC_OFFSET_SEC;
    } catch (err) {
        return DEFAULT_SYNC_OFFSET_SEC; // private-browsing/storage-blocked - just use the default, don't break lyrics over it
    }
}

// Binary search for the last line whose startMs <= posMs - lines.length is
// at most a few hundred, but this runs on every tick while playing, so
// linear search would be needlessly wasteful.
function findActiveIndex(lines, posMs) {
    let lo = 0;
    let hi = lines.length - 1;
    let result = -1;
    while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (lines[mid].startMs <= posMs) {
            result = mid;
            lo = mid + 1;
        } else {
            hi = mid - 1;
        }
    }
    // Only "active" while still inside that line's own window - past its
    // endMs (an instrumental gap before the next line even starts) nothing
    // should stay highlighted.
    if (result >= 0 && posMs > lines[result].endMs) return -1;
    return result;
}

// Owns the sync-offset setting - called exactly ONCE, in VoiceRoomPanel.js,
// and threaded down as a prop to both LyricsPanel.js (which renders the
// slider) and LyricsOverlay.js (which just needs the value). This used to
// live inside useLyricsPosition() itself, called independently by both of
// those components - which meant each got its OWN local React state
// initialized from localStorage only once, at its OWN mount time. Adjusting
// the offset in the panel updated the panel's copy and localStorage, but an
// already-mounted (or about to be freshly-mounted-with-stale-timing) overlay
// had no way to find out short of a full remount - a real, reported bug.
// One shared instance instead of two independent copies makes that whole
// class of divergence impossible by construction, not just unlikely.
export function useLyricsSyncOffset() {
    const [offsetSec, setOffsetSecState] = useState(loadStoredOffset);

    const setOffsetSec = useCallback((next) => {
        setOffsetSecState(next);
        try {
            localStorage.setItem(OFFSET_STORAGE_KEY, String(next));
        } catch (err) {
            // private-browsing/storage-blocked - the offset still works for
            // the rest of this session via state, it just won't persist.
        }
    }, []);

    return { offsetSec, setOffsetSec };
}

// musicState: the room's musicState object (status/positionSec/updatedAt/
// lyrics) - the same shape LyricsPanel.js and LyricsOverlay.js both receive.
// offsetSec: from useLyricsSyncOffset() above (shared, not owned here).
// Returns {lines, lyricsStatus, plainText, activeIndex}.
export function useLyricsPosition(musicState, offsetSec) {
    const status = musicState && musicState.status;
    const positionSec = (musicState && musicState.positionSec) || 0;
    const updatedAt = musicState && musicState.updatedAt;
    // Not surfaced while 'error'/'idle' - a lyrics lookup can still resolve
    // and land on musicSession.js's this.lyrics even after a track failed
    // to connect (nowPlaying isn't cleared on a connect failure), which
    // would otherwise show lyrics for a song that never actually played.
    const lyrics = status !== 'error' && status !== 'idle' ? musicState && musicState.lyrics : null;

    // Only ever read as a useMemo dependency below (to force a recompute on
    // each tick) - its actual value is never used.
    const [tick, setTick] = useState(0);

    // Anchored to THIS client's own Date.now() at the moment the update was
    // received, NOT `positionSec + (Date.now() - updatedAt)/1000` using the
    // server's updatedAt directly - VideoWatchOverlay.js hit exactly that
    // clock-skew bug (an unsynced OS clock produced 10+ second gaps between
    // viewers) and fixed it this same way: both Date.now() reads below come
    // from this one client's own clock, so any constant skew against the
    // server cancels out in the subtraction instead of leaking into the
    // displayed position.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const localAnchor = useMemo(() => ({ at: Date.now(), positionSec }), [updatedAt, positionSec]);

    const expectedSec = useCallback(() => {
        const raw = status === 'playing' ? localAnchor.positionSec + (Date.now() - localAnchor.at) / 1000 : localAnchor.positionSec;
        return raw - offsetSec;
    }, [status, localAnchor, offsetSec]);

    // Re-renders on a fixed tick only while actually advancing - paused/idle
    // has nothing moving to re-check, and a backgrounded tab skips ticks
    // entirely so a room nobody's looking at doesn't burn CPU on it.
    useEffect(() => {
        if (status !== 'playing') return undefined;
        const id = setInterval(() => {
            if (document.hidden) return;
            setTick((n) => n + 1);
        }, TICK_MS);
        return () => clearInterval(id);
    }, [status]);

    const lines = (lyrics && lyrics.lines) || null;

    const activeIndex = useMemo(() => {
        if (!lines || !lines.length) return -1;
        return findActiveIndex(lines, expectedSec() * 1000);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lines, expectedSec, tick]);

    return {
        lines,
        lyricsStatus: lyrics ? lyrics.status : null,
        plainText: lyrics ? lyrics.text : null,
        activeIndex
    };
}

export { DEFAULT_SYNC_OFFSET_SEC };
